import * as THREE from 'three';

import { FaceVertices, FaceIndices } from '@/plugin/ffd/adapter/GeometryAdapter';
import { calculateSimilarVertexForAdapter, GeometryAdapterInterface } from '@/plugin/ffd/adapter/GeometryAdapterFactory';

/* eslint-disable @typescript-eslint/no-explicit-any*/
export default class UnIndexedBufferGeometryAdapter implements GeometryAdapterInterface {
  protected bufferGeometry: THREE.BufferGeometry;
  protected positionAttribute: THREE.BufferAttribute;
  protected normalAttribute: THREE.BufferAttribute | null;
  similarVertex: number[][];

  constructor(bufferGeometry: THREE.BufferGeometry, calculateSimilarVertex = false) {
    this.bufferGeometry = bufferGeometry;
    this.positionAttribute = bufferGeometry.attributes.position as THREE.BufferAttribute;
    this.normalAttribute = bufferGeometry.attributes.normal as THREE.BufferAttribute;
    
    this.similarVertex = [...Array(this.numVertices)].map(() => []);
    if (calculateSimilarVertex) calculateSimilarVertexForAdapter(this);
  }

  get vertices(): THREE.Vector3[] {
    const result: THREE.Vector3[] = [];
    for (let index = 0; index < this.numVertices; index++) {
      result.push(this.getVertex(index));
    }
    return result;
  }

  get numVertices(): number {
    return this.positionAttribute.count;
  }

  get numFaces(): number {
    return this.numVertices / 3;
  }

  setVertex(index: number, x: number, y: number, z: number): void {
    this.positionAttribute.setXYZ(index, x, y, z);
  }

  setVertexX(index: number, x: number): void {
    this.positionAttribute.setX(index, x);
  }

  setVertexY(index: number, y: number): void {
    this.positionAttribute.setY(index, y);
  }

  setVertexZ(index: number, z: number): void {
    this.positionAttribute.setZ(index, z);
  }

  public getVertex(index: number): THREE.Vector3 {
    return new THREE.Vector3().fromBufferAttribute(this.positionAttribute, index);
  }

  getVertexX(index: number): number {
    return this.positionAttribute.getX(index);
  }

  getVertexY(index: number): number {
    return this.positionAttribute.getY(index);
  }

  getVertexZ(index: number): number {
    return this.positionAttribute.getZ(index);
  }

  public getNormal(index: number): THREE.Vector3 {
    if (this.normalAttribute) {
      return new THREE.Vector3().fromBufferAttribute(this.normalAttribute, index);
    }
    return new THREE.Vector3(0, 0, 0);
  }

  getFace(index: number): FaceIndices {
    const offsetPosition = 3 * index;

    return {
      a: offsetPosition,
      b: offsetPosition + 1,
      c: offsetPosition + 2,
    };
  }

  getFaceVertices(index: number): FaceVertices {
    const offsetPosition = 3 * index;

    return {
      a: this.getVertex(offsetPosition),
      b: this.getVertex(offsetPosition + 1),
      c: this.getVertex(offsetPosition + 2),
    };
  }

  updateVertices(): void {
    this.positionAttribute.needsUpdate = true;
    if (this.normalAttribute) {
        this.normalAttribute.needsUpdate = true;
        this.bufferGeometry.computeVertexNormals();
    }
  }

  updateFaces(): void {
    this.updateVertices();
  }

  getBoundingBox(): THREE.Box3 | null {
    this.bufferGeometry.computeBoundingBox();

    return this.bufferGeometry.boundingBox;
  }
}
